import React, { useEffect, useState } from "react";
import {
  Box,
  Card,
  IconButton,
  List,
  ListItem,
  ListItemText,
  Tooltip,
  Typography,
  Button,
  Alert,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
} from "@mui/material";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import CheckIcon from "@mui/icons-material/Check";
import VisibilityIcon from "@mui/icons-material/Visibility";
import VisibilityOffIcon from "@mui/icons-material/VisibilityOff";
import CodeIcon from "@mui/icons-material/Code";
import { useSnackbar } from "notistack";
import axios from "axios";
import DashboardDetails from "../components/DashboardDetails";
import { useNavigate } from "react-router-dom";
import { useAuth, useUser } from "@clerk/clerk-react";

const Dashboard = () => {
  const { enqueueSnackbar } = useSnackbar();
  const [copiedIndex, setCopiedIndex] = useState(null);
  const [isApiKeyCopied, setIsApiKeyCopied] = useState(false);
  const [data, setData] = useState([]);
  const [jsonData, setJsonData] = useState([]);
  const [isApiKeyVisible, setIsApiKeyVisible] = useState(false);
  const [currentStep, setCurrentStep] = useState("list");
  const [selectedApi, setSelectedApi] = useState(null);
  const [isDialogOpen, setIsDialogOpen] = useState(false);
  const [name, setName] = useState("");
  const [description, setDescription] = useState("");
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false); // Loading state

  const navigate = useNavigate();
  const { getToken } = useAuth();
  const { user } = useUser();

  useEffect(() => {
    const fetchData = async () => {
      if (user && user.id) {
        const token = await getToken();
        const id = user.id;
        setLoading(true);
        try {
          const res = await axios.get(
            `https://faker-2yvr.onrender.com/api/items/${id}`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );
          console.log(res.data);
          setData(res.data);
        } catch (error) {
          console.error("Error fetching data:", error);
          setError("Error fetching data");
        } finally {
          setLoading(false); // Stop loading
        }
      }
    };

    fetchData();
  }, [getToken, user]);

  useEffect(() => {
    if (error) {
      const timer = setTimeout(() => setError(""), 10000);
      return () => clearTimeout(timer);
    }
  }, [error]);

  const handleCopy = (text, index = null) => {
    navigator.clipboard.writeText(text);
    enqueueSnackbar("Copied to clipboard!", { variant: "success" });
    if (index !== null) {
      setCopiedIndex(index);
      setTimeout(() => setCopiedIndex(null), 1000);
    } else {
      setIsApiKeyCopied(true);
      setTimeout(() => setIsApiKeyCopied(false), 1000);
    }
  };

  const handleFileUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      const reader = new FileReader();
      reader.onload = async (e) => {
        try {
          const jsonData = JSON.parse(e.target.result);

          if (!Array.isArray(jsonData)) {
            setError("We only accept arrays in JSON files");
            return;
          }

          setJsonData(jsonData);
          setIsDialogOpen(true);
        } catch (error) {
          console.error("Error parsing JSON:", error);
          setError("Failed to parse JSON file");
        }
      };
      reader.readAsText(file);
    }
  };

  const handleDialogClose = () => {
    setIsDialogOpen(false);
    setName("");
    setDescription("");
  };

  const handleDialogSubmit = async () => {
    const token = await getToken();

    await axios.post(
      "https://faker-2yvr.onrender.com/api/items",
      {
        title: name,
        description: description,
        jsonData: jsonData,
        creator: user.id,
        schema: {},
      },
      {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      }
    );
    setIsDialogOpen(false);
  };

  const handleShowApiDetails = (api) => {
    setSelectedApi(api);
    setCurrentStep("details");
  };

  const handleNavigateGenerate = () => {
    navigate("/generate");
  };

  const uploadedApiKey = data && data[0]?.apiKey;

  return (
    <Box sx={{ p: 4 }}>
      <Card sx={{ p: 3, boxShadow: "rgba(0, 0, 0, 0.1) 0px 15px 30px" }}>
        {error && (
          <Alert severity="error" sx={{ mb: 3 }}>
            {error}
          </Alert>
        )}

        {loading ? (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 4 }}>
            <CircularProgress />
          </Box>
        ) : currentStep === "list" ? (
          <>
            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "space-between",
              }}
            >
              <Typography variant="h5" gutterBottom>
                MANAGE YOUR API
              </Typography>
              <Box>
                <Button
                  variant="contained"
                  component="label"
                  color="primary"
                  sx={{ borderRadius: 0.4 }}
                >
                  Upload File
                  <input
                    type="file"
                    hidden
                    accept=".json,.csv,.xml,.yaml,.yml,.txt,.xlsx,.js"
                    onChange={handleFileUpload}
                  />
                </Button>
                <Button
                  variant="contained"
                  color="secondary"
                  onClick={handleNavigateGenerate}
                  sx={{ ml: 3, borderRadius: 0.4 }}
                >
                  Generate New Data
                </Button>
              </Box>
            </Box>

            <Alert sx={{ mt: 3, p: 0 }} severity="info">
              <strong>Note:</strong> Upload a file in one of these formats:{" "}
              <strong>.json, .csv, .xml, .yaml, .yml, .txt, .xlsx</strong>.
              You’ll receive an API link for the uploaded data.
            </Alert>

            <Box
              sx={{
                display: "flex",
                alignItems: "center",
                gap: 2,
                justifyContent: "flex-end",
              }}
            >
              <Box
                sx={{
                  display: "flex",
                  alignItems: "center",
                  gap: 1,
                  backgroundColor: "#f9f9f9",
                  borderRadius: "4px",
                  padding: "6px 12px",
                }}
              >
                <Typography
                  variant="body2"
                  sx={{ color: "text.secondary", fontFamily: "monospace" }}
                >
                  API KEY:
                </Typography>
                <Typography
                  variant="body2"
                  sx={{
                    color: "#333",
                    fontFamily: "monospace",
                    filter: isApiKeyVisible ? "none" : "blur(6px)",
                    userSelect: "none",
                  }}
                >
                  {uploadedApiKey}
                </Typography>
                <IconButton
                  size="small"
                  onClick={() => setIsApiKeyVisible(!isApiKeyVisible)}
                >
                  {isApiKeyVisible ? <VisibilityOffIcon /> : <VisibilityIcon />}
                </IconButton>

                <Tooltip
                  sx={{ opacity: isApiKeyVisible ? 1 : 0 }}
                  title="Copy API Key"
                >
                  <IconButton
                    onClick={() => handleCopy(uploadedApiKey)}
                    color="primary"
                    size="small"
                  >
                    {isApiKeyCopied ? <CheckIcon /> : <ContentCopyIcon />}
                  </IconButton>
                </Tooltip>
              </Box>
            </Box>

            <List sx={{ mt: 3 }}>
              {data.map((link, index) => (
                <ListItem
                  key={index}
                  sx={{
                    display: "flex",
                    justifyContent: "space-between",
                    borderBottom: "1px solid #e0e0e0",
                    py: 1,
                  }}
                >
                  <ListItemText
                    primary={link.title}
                    secondary={link.dataApi}
                    primaryTypographyProps={{
                      style: {
                        color: "#000000",
                        fontWeight: "bold",
                        textTransform: "capitalize",
                      },
                    }}
                  />
                  <Box sx={{ display: "flex", gap: 1 }}>
                    <Tooltip title="Copy link">
                      <IconButton
                        onClick={() => handleCopy(link.dataApi, index)}
                        color="primary"
                      >
                        {copiedIndex === index ? (
                          <CheckIcon />
                        ) : (
                          <ContentCopyIcon />
                        )}
                      </IconButton>
                    </Tooltip>
                    <Tooltip title="API Details">
                      <IconButton
                        onClick={() => handleShowApiDetails(link)}
                        color="primary"
                      >
                        <CodeIcon />
                      </IconButton>
                    </Tooltip>
                  </Box>
                </ListItem>
              ))}
            </List>
          </>
        ) : (
          <DashboardDetails
            data={selectedApi}
            onBack={() => setCurrentStep("list")}
          />
        )}
      </Card>

      <Dialog open={isDialogOpen} onClose={handleDialogClose}>
        <DialogTitle>Enter File Name</DialogTitle>
        <DialogContent>
          <TextField
            label="Name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            fullWidth
            sx={{ mb: 2 }}
          />
          <TextField
            label="Description"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            fullWidth
            multiline
            rows={3}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDialogClose} color="secondary">
            Cancel
          </Button>
          <Button
            onClick={handleDialogSubmit}
            variant="contained"
            color="primary"
          >
            Submit
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default Dashboard;
